import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { Habit, HabitEntry } from '../habit.service';
import { AuthService } from '../auth.service';

@Injectable({
    providedIn: 'root'
})
export class MockHabitService {
    private authService = inject(AuthService);

    private habitsSubject = new BehaviorSubject<Habit[]>([]);
    habits$ = this.habitsSubject.asObservable();

    private entriesSubject = new BehaviorSubject<HabitEntry[]>([]);
    entries$ = this.entriesSubject.asObservable();

    private habits: Habit[] = [];
    private entries: HabitEntry[] = [];

    private readonly HABITS_KEY = 'mock_habits';
    private readonly ENTRIES_KEY = 'mock_entries';

    constructor() {
        // Load initial data
        const storedHabits = localStorage.getItem(this.HABITS_KEY);
        if (storedHabits) {
            this.habits = JSON.parse(storedHabits);
            this.habitsSubject.next(this.habits);
        }
        const storedEntries = localStorage.getItem(this.ENTRIES_KEY);
        if (storedEntries) {
            this.entries = JSON.parse(storedEntries);
            this.entriesSubject.next(this.entries);
        }

        this.authService.user$.subscribe(user => {
            if (!user) {
                this.habits = [];
                this.entries = [];
                this.saveHabits();
                this.saveEntries();
            }
        });
    }

    private saveHabits() {
        localStorage.setItem(this.HABITS_KEY, JSON.stringify(this.habits));
        this.habitsSubject.next([...this.habits]);
    }

    private saveEntries() {
        localStorage.setItem(this.ENTRIES_KEY, JSON.stringify(this.entries));
        this.entriesSubject.next([...this.entries]);
    }

    async fetchHabits() {
        return Promise.resolve();
    }

    async addHabit(habit: Omit<Habit, 'id' | 'userId' | 'createdAt'>) {
        const newHabit: Habit = {
            id: 'mock-habit-' + Date.now(),
            userId: 'test-user-id',
            createdAt: new Date(),
            order: this.habits.length,
            ...habit
        } as Habit;

        this.habits.push(newHabit);
        this.habitsSubject.next([...this.habits]);
        return Promise.resolve(newHabit.id);
    }

    async updateHabit(id: string, updates: Partial<Habit>) {
        const idx = this.habits.findIndex(h => h.id === id);
        if (idx > -1) {
            this.habits[idx] = { ...this.habits[idx], ...updates };
            this.habitsSubject.next([...this.habits]);
        }
        return Promise.resolve();
    }

    async deleteHabit(id: string) {
        this.habits = this.habits.filter(h => h.id !== id);
        this.habitsSubject.next([...this.habits]);
        return Promise.resolve();
    }

    async fetchEntries(month: string) {
        const start = `${month}-01`;
        const end = `${month}-31`;
        const filtered = this.entries.filter(e => e.date >= start && e.date <= end);
        this.entriesSubject.next(filtered);
    }

    async setEntry(date: string, habitId: string, value: number, status: any = 'completed', comment?: string) {
        const newEntry: HabitEntry = {
            id: `mock-entry-${habitId}-${date}`,
            habitId,
            date,
            value,
            status,
            comment,
            userId: 'test-user-id'
        };
        this.entries = this.entries.filter(e => !(e.habitId === habitId && e.date === date));
        this.entries.push(newEntry);
        this.entriesSubject.next([...this.entries]);
        return Promise.resolve();
    }

    async markWeekendsLeave(habitId: string, month: string) {
        return Promise.resolve();
    }
}
